import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { finalize } from 'rxjs';
import { Permissoes, TaxaIdeal, TaxasIdeais, Operacao, OperacoesDisponiveis } from 'app/interfaces';
import { ClienteService, PermissoesService, OperacaoService, DialogService } from 'app/services';
import { CapitalizePipe } from '../../../pipes/capitalize/capitalize.pipe';
import { LoadingComponent, FiltroClientesComponent } from 'app/components';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TAXAS_SELECT } from 'app/constants/taxas.constants';

@Component({
  selector: 'app-listar-taxas',
  standalone: true,
  imports: [CommonModule, MatTableModule, RouterLink, MatTooltipModule, FiltroClientesComponent, CapitalizePipe, LoadingComponent, TranslocoModule],
  templateUrl: './listar-taxas-ideais.component.html',
  styleUrl: './listar-taxas-ideais.component.css'
})

export class ListarTaxasIdeaisComponent implements OnInit {
  public operacoesDisponiveis: OperacoesDisponiveis = [];
  public taxasIdeais: TaxasIdeais = [];
  public clienteId!: number;
  public operacaoId!: number;
  public serviceId!: string;
  public loading = false;
  public dataSource: MatTableDataSource<TaxaIdeal> = new MatTableDataSource<TaxaIdeal>();
  public displayedColumns: string[] = [
    'vc_service_id',
    'en_tipo',
    'fl_minimo_aceitavel',
    'fl_ideal',
    'acoes'
  ];

  public permissoes: Permissoes.Acessos = {
    criar: false,
    editar: false,
    excluir: false,
    exibirAcoes: false
  };

  public tiposTaxas = TAXAS_SELECT.map(t => ({
    tipo: t.dado
  }));

  public coresTipos: Record<string, string> = {
    liquido_1: '#DBF2FF',
    liquido_2: '#DBF2FF',
    liquido_3: '#DBF2FF',
    solido_1: '#D652F747',
    solido_2: '#D652F747',
    solido_3: '#D652F747',
    profundidade: '#FFF3C5',
    muda_ton_ha: '#FFCDCD',
    muda_tol_m: '#FFE4BF'
  };

  public medidasTaxas: Record<string, string> = {
    solido_1: 'kg/m',
    solido_2: 'kg/m',
    solido_3: 'kg/m',
    liquido_1: 'L/ha',
    liquido_2: 'L/ha',
    liquido_3: 'L/ha',
    profundidade: 'm',
    muda_tol_m: 'tol/m',
    muda_ton_ha: 'ton/ha'
  };

  private readonly PERMISSAO_CRIAR = 'editar_operacoes_premoplan';
  private readonly PERMISSAO_EDITAR = 'editar_operacoes_premoplan';
  private readonly PERMISSAO_EXCLUIR = 'editar_operacoes_premoplan';

  constructor(
    private readonly dialogService: DialogService,
    private readonly permissoesService: PermissoesService,
    private readonly operacaoService: OperacaoService,
    private readonly clienteService: ClienteService,
    private readonly transloco: TranslocoService
  ) {}

  public ngOnInit(): void {
    this.verificarPermissoes();
    this.criarFiltroListagem();
  }

  private criarFiltroListagem(): void {
    this.dataSource.filterPredicate = (data, filter) =>
      filter === 'todos' || data.en_tipo === filter;
  }

  public filtrarPorTipo(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.dataSource.filter = value;
  }

  public buscarOperacoesDisponiveis(clienteId: number): void {
    this.loading = true;
    this.dataSource.data = [];

    this.clienteService.buscarOperacoesDisponiveis(clienteId)
      .pipe(finalize(() => this.loading = false))
      .subscribe(operacoes => {
        this.operacoesDisponiveis = operacoes;

        if (operacoes?.length) {
          this.buscarTaxasPorOperacao(operacoes[0].sr_id);
        }
      });
  }

  public buscarTaxas(event: Event): void {
    const id = Number((event.target as HTMLSelectElement).value);
    this.buscarTaxasPorOperacao(id);
  }

  private buscarTaxasPorOperacao(idOperacao: number): void {
    this.operacaoId = idOperacao;
    this.loading = true;
    this.dataSource.data = [];

    this.operacaoService.buscarTaxas(idOperacao)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (operacao: Operacao) => {
          this.serviceId = operacao.vc_service_id;
          this.taxasIdeais = operacao.taxas_ideais ?? [];
          this.dataSource.data = this.taxasIdeais;
        },
        error: () => {
          this.dialogService.erro(
            this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.loadErrorTitle'),
            this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.loadErrorMessage')
          );
        }
      });
  }

  public verificarPermissoes(): void {
    this.permissoes.criar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_CRIAR);
    this.permissoes.editar = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EDITAR);
    this.permissoes.excluir = this.permissoesService.usuarioTemPermissao(this.PERMISSAO_EXCLUIR);
    this.permissoes.exibirAcoes = this.permissoes.editar || this.permissoes.excluir;
  }

  public async excluir(idTaxa: number): Promise<void> {
    const confirmou = await this.dialogService.confirmacao(
      this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.deleteTitle'),
      this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.deleteConfirm')
    );

    if (!confirmou) return;

    this.dialogService.loading(
      this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.deleting')
    );

    this.operacaoService.deletarTaxas(idTaxa).subscribe({
      next: async () => {
        await this.dialogService.sucesso(
          this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.successTitle'),
          this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.successMessage')
        );
        this.atualizarTabela(idTaxa);
      },
      error: () => {
        this.dialogService.erro(
          this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.errorTitle'),
          this.transloco.translate('COMPONENTS.TAXAS_MAPA_LIST.dialogs.errorMessage')
        );
      }
    });
  }

  public atualizarTabela(id: number): void {
    this.taxasIdeais = this.taxasIdeais.filter(t => t.sr_id !== id);
    this.dataSource.data = this.taxasIdeais;
  }
}
